/**
 * Contributors
 *
 * Component to handle the carousel, grid, and modal renderings.
 */

/**
 * React dependencies
 */

import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * DWT dependencies
 */

import { ModalContext } from '../Modal/ModalContext';
import { ContributorsContext } from './ContributorsContext';

/**
 * DWT hooks
 */

import useFetchPRHData from '../../hooks/useFetchPRHData';
import useModal from '../../hooks/useModal';

/**
 * DWT components
 */

import Carousel from '../Carousel';
import FilterContainer from '../Filter/FilterContainer';
import Grid from '../Grid';
import LoadMoreButton from '../LoadMoreButton';
import Modal from '../Modal/Modal';
import NoResults from '../NoResults';

const getContribRoleCode = () => {
  const contributorRoles = window.DP?.contributor_roles || [];
  return contributorRoles.map( role => role.value ).join( ',' );
};

const Contributors = () => {
  const c = useContext( ContributorsContext );
  const [ context, setContext ] = useState( c );
  
  const contribRoleCode = getContribRoleCode();
  
  const carousel = context.module === 'carousel';
  const grid = context.module === 'grid';
  const showMetadata = context.showMetadata === 'true';
  const modalRef = useRef( null );

  // Use a memoized version of the combined context object for the API call
  const apiContext = useMemo( () => {
    return {
      ...context,
      contribRoleCode
    };
  }, [ context, contribRoleCode ] );

  const {
    makeRequest,
    setStart,
    recordCount,
    loading,
    error,
    data
  } = useFetchPRHData( apiContext, '/wp-json/dwt/v2/list-author' );

  const {
    modalClass,
    modalData,
    onClickOpenModal,
    onClickCloseModal
  } = useModal( data, modalRef, context );

  useEffect( () => {
    const targetNode = document.querySelector( '[ data-feature-by="contributors" ]' );
    const config = { attributes: true, childList: false, subtree: false };

    const callback = ( mutationList ) => {
      for ( const mutation of mutationList ) {
        if ( mutation.type === 'attributes' ) {
          setContext({ ...mutation.target.dataset });
        }
      }
    };

    const observer = new MutationObserver( callback );
    observer.observe( targetNode, config );
  }, [] );

  const handleErrorMsg = () => context.listType === 'full-list' ? 'Sorry, we are unable to find what you\'re looking for. Try adjusting the filters.' : 'Sorry something happened. Please refresh the page.';

  return (
    <>
      <ModalContext.Provider value={{ onClickOpenModal }}>
        { context.listType === 'full-list' && (
          <FilterContainer context={ context } recordCount={ recordCount } setStart={ setStart } error={ error } />
        ) }

        <div className='list-wrapper'>

          { error && (
            <NoResults handleErrorMsg={ handleErrorMsg() } />
          ) }

          { carousel && (
            <Carousel
              data={ data }
              context={ context }
              showMetadata={ showMetadata }
              error={ error }
              loading={ loading }
            />
          ) }

          { grid && (
            <Grid
              data={ data }
              context={ context }
              showMetadata={ showMetadata }
              error={ error }
              loading={ loading }
            />
          ) }

          { !error && context.listType === 'full-list' && data?.length < recordCount && (
            <LoadMoreButton makeRequest={ makeRequest } loading={ loading } />
          ) }

          <Modal
            modalData={ modalData }
            modalClass={ modalClass }
            context={ context }
            onClose={ onClickCloseModal }
            modalType={ context.featureBy }
            ref={ modalRef }
          />
        </div>
      </ModalContext.Provider>
    </>
  );
};

const renderContributors = () => {
  const els = document.querySelectorAll( '[data-feature-by="contributors"]' );

  if ( !els ) {
    return;
  }

  for ( const el of els ) {
    const root = createRoot( el );

    root.render(
      <ContributorsContext.Provider value={ { ...el.dataset } }>
        <Contributors />
      </ContributorsContext.Provider>
    );
  }
};

export default renderContributors;
